import React, { ReactElement } from 'react';
import { centsToCurrency } from '../../../../utils/centsToCurrency';
import { Box, HStack, Icon, Text, TextProps } from '@medely/ui-kit';
import { useCurrentUser } from '../../../../hooks/useCurrentUser';
import { JobDetailsListItem } from '../../JobDetailsListItem';

type ParkingInfoSectionProps = {
  parkingInstructions?: string | null;
  parkingCostCents?: number | null;
  parkingReimbursed?: boolean | null;
  parkingReimbursementCents?: number | null;
  parkingFree?: boolean | null;
  parkingValidated?: boolean | null;
  bookedOrInProgress: boolean;
  smallIcon?: boolean;
};

type ParkingInformationItemProps = {
  text: TextProps['children'];
};

const ParkingInformationItem = ({ text }: ParkingInformationItemProps) => {
  return (
    <HStack my={2} gap={1}>
      <Icon name="check" iconSize="20px" />
      <Text size="m">{text}</Text>
    </HStack>
  );
};

const ParkingInfoSection = ({
  parkingInstructions,
  parkingCostCents,
  parkingReimbursed,
  parkingReimbursementCents,
  parkingFree,
  parkingValidated,
  bookedOrInProgress,
  smallIcon = false,
}: ParkingInfoSectionProps): ReactElement => {
  const parkingDetails = {
    parkingInstructions,
    parkingCostCents,
    parkingReimbursed,
    parkingReimbursementCents,
    parkingFree,
    parkingValidated,
  };

  const { isMarketplace } = useCurrentUser();
  const showParkingReimbursement = parkingReimbursed && isMarketplace;
  const parkingReimbursementCentsText = !!parkingReimbursementCents
    ? `(${centsToCurrency(parkingReimbursementCents)})`
    : '';

  const hasNoParkingDetails = Object.values(parkingDetails).some((value) => !value);

  if (hasNoParkingDetails) {
    return <></>;
  }

  return (
    <JobDetailsListItem
      title="Parking"
      icon={
        <Icon
          name="light-parking-circle"
          iconSize={smallIcon ? '14px' : '20px'}
          color="text.primary"
          variant="custom"
        />
      }
    >
      <Box mt={1}>
        <Text size="m" color="text.secondary" testId="job-additional-details-parking-instructions">
          {bookedOrInProgress && parkingInstructions}
        </Text>
        {!!parkingCostCents && (
          <ParkingInformationItem text={`Parking cost (${centsToCurrency(parkingCostCents)})`} />
        )}
        {showParkingReimbursement && (
          <ParkingInformationItem text={`Parking reimbursed ${parkingReimbursementCentsText}`} />
        )}

        {parkingFree && <ParkingInformationItem text="Free parking on premises" />}
        {parkingValidated && <ParkingInformationItem text="Parking validated" />}
      </Box>
    </JobDetailsListItem>
  );
};

export default ParkingInfoSection;
